import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity'
import { createReducer, on, Action } from '@ngrx/store'

import * as ProjectorActions from './projectors.actions'
import { ProjectorsEntity } from './projectors.models'
import { ProjectorTemplatesEntity } from 'libs/gb-dashboard/presenter/data-access-projector-templates/src/lib/+state/projector-templates.models'

export const PROJECTORS_FEATURE_KEY = 'projectors'

export interface ProjectorsState extends EntityState<ProjectorsEntity> {
  selectedId?: string
  loaded: boolean
  projectorTemplates?: ProjectorTemplatesEntity[]
  editCreateLoaded: boolean
  templatesLoaded: boolean
  presenterEditToken?: string
}
export const projectorsAdapter: EntityAdapter<ProjectorsEntity> = createEntityAdapter<ProjectorsEntity>({
  selectId: (projector: ProjectorsEntity) => projector.uuid,
  sortComparer: (a: ProjectorsEntity, b: ProjectorsEntity) => {
    const orderA = a.order ?? 0
    const orderB = b.order ?? 0

    if (orderA === orderB) {
      return a.created < b.created ? -1 : 1
    }

    return orderA < orderB ? -1 : 1
  },
})

export const initialProjectorsState: ProjectorsState = projectorsAdapter.getInitialState({
  loaded: true,
  editCreateLoaded: true,
  templatesLoaded: true,
})

const reducer = createReducer(
  initialProjectorsState,
  on(ProjectorActions.loadProjectors, (state) => ({
    ...state,
    loaded: false,
  })),
  on(ProjectorActions.loadProjectorsSuccess, (state, { projectors }) =>
    projectorsAdapter.setAll(projectors, { ...state, loaded: true }),
  ),
  on(ProjectorActions.setSelectedProjectorLocal, (state, { projector }) =>
    projectorsAdapter.upsertOne(projector, {
      ...state,
      selectedId: projector.uuid,
      editCreateLoaded: true,
    }),
  ),
  on(ProjectorActions.patchSelectedProjectorLocal, (state, { projector }) => {
    if (!state.selectedId) {
      return state
    }

    return projectorsAdapter.updateOne(
      {
        id: state.selectedId,
        changes: projector,
      },
      {
        ...state,
        editCreateLoaded: true,
      },
    )
  }),
  on(ProjectorActions.updateProjector, ProjectorActions.updateSelectedProjector, (state) => ({
    ...state,
    editCreateLoaded: false,
  })),
  on(ProjectorActions.addProjector, (state) => ({
    ...state,
    editCreateLoaded: false,
  })),
  on(ProjectorActions.addSlide, (state) => ({
    ...state,
    editCreateLoaded: false,
  })),
  on(ProjectorActions.reorderSlides, (state) => ({
    ...state,
    editCreateLoaded: false,
  })),
  on(ProjectorActions.selectProjector, (state, action) => ({ ...state, selectedId: action.uuid })),
  on(ProjectorActions.updateProjectorSuccess, (state, action) =>
    projectorsAdapter.updateOne(action.update, { ...state, editCreateLoaded: true }),
  ),
  on(ProjectorActions.reorderSlidesSuccess, (state, action) =>
    projectorsAdapter.updateMany(
      action.slides.map((slide) => ({
        id: slide.uuid,
        changes: slide,
      })),
      { ...state, editCreateLoaded: true },
    ),
  ),
  on(ProjectorActions.updateProjectorFailure, ProjectorActions.updateProjectorSuccess, (state) => ({
    ...state,
    editCreateLoaded: true,
  })),
  on(ProjectorActions.reorderSlidesFailure, (state) => ({
    ...state,
    editCreateLoaded: true,
  })),
  on(ProjectorActions.loadProjectorTemplates, (state) => ({
    ...state,
    templatesLoaded: false,
  })),
  on(ProjectorActions.loadProjectorTemplatesSuccess, (state, { templates }) => ({
    ...state,
    projectorTemplates: templates,
    templatesLoaded: true,
  })),

  on(ProjectorActions.updateProjectorTemplateSuccess, (state, { template }) => ({
    ...state,
    projectorTemplates: (state.projectorTemplates ?? []).map((t) => (t.uuid === template.uuid ? template : t)),
  })),

  on(ProjectorActions.addProjectorSuccess, (state, action) =>
    projectorsAdapter.addOne(action.projector, { ...state, editCreateLoaded: true }),
  ),
  on(ProjectorActions.addProjectorFailure, (state) => ({
    ...state,
    editCreateLoaded: true,
  })),
  on(ProjectorActions.addSlideSuccess, (state, action) =>
    projectorsAdapter.addOne(action.projector, { ...state, editCreateLoaded: true }),
  ),
  on(ProjectorActions.addSlideFailure, (state) => ({
    ...state,
    editCreateLoaded: true,
  })),
  on(ProjectorActions.openAddEditProjectorDialog, (state, action) => ({
    ...state,
    selectedId: action.uuid,
    editCreateLoaded: true,
  })),
  on(ProjectorActions.deleteProjectorSuccess, ProjectorActions.deleteSlideSuccess, (state, action) =>
    projectorsAdapter.removeOne(action.uuid, state),
  ),
  on(ProjectorActions.generatePresenterTokenSuccess, (state, action) => ({
    ...state,
    presenterEditToken: action.otu_token,
  })),
  on(ProjectorActions.deleteProjectorTemplateSuccess, (state, action) => ({
    ...state,
    projectorTemplates: state.projectorTemplates?.filter((item) => item.uuid !== action.uuid),
  })),
)

export function projectorsReducer(state: ProjectorsState | undefined, action: Action) {
  return reducer(state, action)
}
